import React from 'react';
import { Skeleton } from '@mantine/core';
import { motion } from 'framer-motion';
import { FADE, FADE_UP, STAGGER } from '@/theme/motion';
import type { TypeListLayout, TypeListSkeleton } from '@/types/list/types.list';

type Props = {
    layout: TypeListLayout;
    columns: number;
    skeleton?: TypeListSkeleton;
    label: string;
};

const RowShape: React.FC<{ index: number; leading: boolean }> = ({ index, leading }) => (
    <div className="bd-li">
        {leading ? <div className="bd-li-leading"><Skeleton height={32} circle /></div> : null}
        <div className="bd-li-main">
            <Skeleton height={10} width={`${38 + ((index * 13) % 30)}%`} radius="sm" />
            <Skeleton height={8} mt={8} width={`${22 + ((index * 17) % 28)}%`} radius="sm" />
        </div>
        <div className="bd-li-meta"><Skeleton height={8} width={48} radius="sm" /></div>
    </div>
);

const CardShape: React.FC<{ index: number }> = ({ index }) => (
    <div style={{ width: '100%' }}>
        <Skeleton height={12} width={`${50 + ((index * 11) % 30)}%`} radius="sm" />
        <Skeleton height={8} mt={10} width="90%" radius="sm" />
        <Skeleton height={8} mt={6} width={`${40 + ((index * 7) % 35)}%`} radius="sm" />
    </div>
);

const ListLoading: React.FC<Props> = ({ layout, columns, skeleton, label }) => {
    const count = Math.max(1, skeleton?.rows ?? (layout.type === 'grid' ? columns * 2 : 6));
    const leading = skeleton?.leading ?? true;
    const slots = Array.from({ length: count }, (_, index) => index);

    if (layout.type === 'grid') {
        return (
            <motion.div variants={FADE} initial="hidden" animate="visible" aria-busy aria-label={label}>
                <div className="bd-list-gridrow" style={{ gridTemplateColumns: `repeat(${Math.max(1, columns)}, minmax(0, 1fr))` }}>
                    {slots.map((index) => (
                        <div key={index} className="bd-list-cell" data-layout="grid"><CardShape index={index} /></div>
                    ))}
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div className="bd-list-run" data-layout="rows" variants={STAGGER} initial="hidden" animate="visible" aria-busy aria-label={label}>
            {slots.map((index) => (
                <motion.div key={index} className="bd-list-cell" data-layout="rows" variants={FADE_UP}>
                    <RowShape index={index} leading={leading} />
                </motion.div>
            ))}
        </motion.div>
    );
};

export default ListLoading;
